import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg, fmtDateTime } from "../../lib/api";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { PageHeader, Badge, Btn, Table, FilterBar, clean } from "../../components/admin/AdminUI";
import { useAdmin, can } from "../../components/admin/AdminLayout";

const FIELDS = [["member_no", "Member ID"], ["full_name", "Full Name", 1], ["email", "Email", 1], ["phone", "Phone", 1], ["age", "Age"], ["referral_source", "Referral Source"],
  ["created_at", "Registered"], ["status", "Verification"], ["eligible_referrals", "Eligible Referrals"], ["reward_tier", "Reward Tier"], ["marketing_consent", "Marketing Consent"], ["referral_code", "Referral Code"]];
const FORMATS = [["xlsx", "Excel (.xlsx)"], ["csv", "CSV (.csv)"], ["pdf", "PDF report (.pdf)"], ["png", "PNG images (.png)"]];

export const download = async (job) => {
  const r = await api.get(`/admin/exports/${job.id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = `cricanalyze_waitlist_${job.id.slice(0, 6)}.${job.file_ext}`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export default function AdminExports() {
  const admin = useAdmin();
  const pii = can(admin, "view_pii");
  const [format, setFormat] = useState("xlsx");
  const [fields, setFields] = useState(FIELDS.filter((x) => pii || !x[2]).map((x) => x[0]));
  const [f, setF] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);
  const loadJobs = () => api.get("/admin/exports").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { loadJobs(); }, []);
  const body = () => ({ format, fields, filters: clean(f) });
  const doPreview = async () => { try { setPreview((await api.post("/admin/exports/preview", body())).data); } catch (e) { toast.error(errMsg(e)); } };
  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/exports", { ...body(), confirm: true }); toast.success(`Export ready: ${data.record_count} records`); setPreview(null); loadJobs(); await download(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const tog = (k) => setFields(fields.includes(k) ? fields.filter((x) => x !== k) : [...fields, k]);
  return (
    <div data-testid="admin-exports">
      <PageHeader title="Export Center" subtitle="Exports are restricted, audit-logged and expire automatically" />
      <div className="card-surface p-6 space-y-6">
        <div><p className="eyebrow mb-3">1 · Format</p>
          <div className="flex flex-wrap gap-2">{FORMATS.map(([k, l]) => <button key={k} onClick={() => setFormat(k)} data-testid={`export-format-${k}`} className={`px-4 py-2 rounded-full text-sm border transition-colors ${format === k ? "border-[#B7FF3C] text-[#B7FF3C] bg-[#B7FF3C]/5" : "border-[#2A362A] text-[#A3ADA3]"}`}>{l}</button>)}</div>
          {format === "png" && <p className="text-xs text-[#7C8A7C] mt-2">PNG exports include a summary chart image plus paginated table images (25 rows each). Multiple pages are delivered as a .zip of real PNG files.</p>}
        </div>
        <div><p className="eyebrow mb-3">2 · Filters</p><FilterBar f={f} set={setF} search={false} /></div>
        <div><p className="eyebrow mb-3">3 · Fields</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">{FIELDS.map(([k, l, isPii]) => (
            <label key={k} className={`flex items-center gap-2 text-sm ${isPii && !pii ? "opacity-40" : ""}`}><Checkbox checked={fields.includes(k)} disabled={isPii && !pii} onCheckedChange={() => tog(k)} data-testid={`export-field-${k}`} />{l}{isPii && <span className="text-[10px] font-mono text-amber-300">PII</span>}</label>))}
          </div>
          <p className="text-xs text-[#7C8A7C] mt-3">OTPs, session tokens, SMTP credentials and other secrets are never included.</p>
        </div>
        <Btn onClick={doPreview} data-testid="export-preview-btn">Review &amp; Export</Btn>
      </div>
      <p className="eyebrow mt-8 mb-3">Export history</p>
      <Table testid="export-jobs" rows={jobs} empty="No exports yet" cols={[["format", "Format", (r) => r.format.toUpperCase()], ["record_count", "Records"], ["filters_text", "Filters"], ["admin_email", "By"],
        ["created_at", "Created", (r) => fmtDateTime(r.created_at)], ["status", "Status", (r) => <Badge s={new Date(r.expires_at) < new Date() ? "expired" : r.status} />],
        ["dl", "", (r) => new Date(r.expires_at) > new Date() && r.status === "ready" ? <Btn variant="ghost" onClick={() => download(r).catch((e) => toast.error(errMsg(e)))} data-testid={`download-${r.id}`}>Download</Btn> : null]]} />
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#111511] border-[#1E281E]" data-testid="export-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Confirm export</DialogTitle></DialogHeader>
          {preview && <div className="space-y-3 text-sm">
            <p>Format: <b>{format.toUpperCase()}</b></p>
            <p>Records: <b className="text-[#B7FF3C]" data-testid="export-record-count">{preview.count}</b></p>
            <p>Filters: <span className="text-[#A3ADA3]">{preview.filters_text}</span></p>
            <p>Fields: <span className="text-[#A3ADA3]">{preview.fields.join(", ")}</span></p>
            <Btn onClick={generate} disabled={busy} className="w-full mt-3" data-testid="export-confirm-btn">{busy ? <Loader2 className="animate-spin" size={16} /> : "Confirm & Generate"}</Btn>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
